/**
 * features/dashboard/Dashboard.tsx
 * Phase 5 — landing dashboard. Live KPIs from invoices, payments,
 * expenses and quotes, a 6-month received-vs-spent chart, and a
 * recent-payments list. Read-only; no new data layer.
 */
import { useMemo } from 'react';
import { TrendingUp, AlertTriangle, Wallet, FileText, FileSignature, ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { useInvoices } from '@/features/invoices/useInvoices';
import { usePayments } from '@/features/payments/usePayments';
import { useExpenses } from '@/features/expenses/useExpenses';
import { useQuotes } from '@/features/quotes/useQuotes';
import { formatPula } from '@/lib/money';
import { Card, Spinner } from '@/components/ui/primitives';

function monthKey(iso: string) {
  return iso.slice(0, 7); // YYYY-MM
}
function lastMonths(n: number) {
  const out: Array<{ key: string; label: string }> = [];
  const d = new Date();
  d.setDate(1);
  for (let i = n - 1; i >= 0; i--) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push({ key: `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, '0')}`, label: m.toLocaleDateString('en-BW', { month: 'short' }) });
  }
  return out;
}

function Kpi({ icon, label, value, sub, tone = 'default' }: { icon: React.ReactNode; label: string; value: string; sub?: string; tone?: 'default' | 'warning' | 'good' | 'bad' }) {
  const toneClass =
    tone === 'warning'
      ? 'text-amber-600 dark:text-amber-400'
      : tone === 'good'
        ? 'text-green-600 dark:text-green-400'
        : tone === 'bad'
          ? 'text-red-600 dark:text-red-400'
          : 'text-primary dark:text-gray-100';
  return (
    <Card className="p-4">
      <div className="mb-2 flex items-center gap-2 text-primaryLight dark:text-gray-400">
        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-brandSoft text-brand">{icon}</span>
        <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
      </div>
      <div className={`text-2xl font-bold ${toneClass}`}>{value}</div>
      {sub && <div className="mt-0.5 text-xs text-primaryLight dark:text-gray-400">{sub}</div>}
    </Card>
  );
}

export function Dashboard() {
  const { invoices, loading: li } = useInvoices();
  const { payments, loading: lp } = usePayments();
  const { expenses, loading: le } = useExpenses();
  const { quotes } = useQuotes();

  const thisMonth = monthKey(new Date().toISOString());
  const today = new Date().toISOString().slice(0, 10);

  const stats = useMemo(() => {
    const open = invoices.filter((i) => i.status === 'issued' || i.status === 'part_paid');
    const receivable = open.reduce((s, i) => s + (i.balance || 0), 0);
    const overdue = open.filter((i) => i.dueDate && i.dueDate < today).reduce((s, i) => s + (i.balance || 0), 0);
    const overdueCount = open.filter((i) => i.dueDate && i.dueDate < today).length;
    const receivedMonth = payments.filter((p) => monthKey(p.date) === thisMonth).reduce((s, p) => s + p.amount, 0);
    const spentMonth = expenses.filter((e) => monthKey(e.date) === thisMonth).reduce((s, e) => s + e.total, 0);
    const pendingQuotes = quotes.filter((q) => q.status === 'sent').length;
    return { open, receivable, overdue, overdueCount, receivedMonth, spentMonth, net: receivedMonth - spentMonth, pendingQuotes };
  }, [invoices, payments, expenses, quotes, thisMonth, today]);

  const months = useMemo(() => lastMonths(6), []);
  const series = useMemo(() => {
    const rec: Record<string, number> = {};
    const spt: Record<string, number> = {};
    payments.forEach((p) => {
      const k = monthKey(p.date);
      rec[k] = (rec[k] || 0) + p.amount;
    });
    expenses.forEach((e) => {
      const k = monthKey(e.date);
      spt[k] = (spt[k] || 0) + e.total;
    });
    const rows = months.map((m) => ({ ...m, received: rec[m.key] || 0, spent: spt[m.key] || 0 }));
    const max = Math.max(1, ...rows.flatMap((r) => [r.received, r.spent]));
    return { rows, max };
  }, [payments, expenses, months]);

  const recent = useMemo(() => payments.slice(0, 6), [payments]);

  if (li || lp || le) return <Spinner label="Loading dashboard…" />;

  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={<FileText size={16} />} label="Receivable" value={formatPula(stats.receivable)} sub={`${stats.open.length} open invoice${stats.open.length === 1 ? '' : 's'}`} />
        <Kpi icon={<AlertTriangle size={16} />} label="Overdue" value={formatPula(stats.overdue)} sub={`${stats.overdueCount} past due`} tone={stats.overdue > 0 ? 'warning' : 'default'} />
        <Kpi icon={<TrendingUp size={16} />} label="Received this month" value={formatPula(stats.receivedMonth)} tone="good" />
        <Kpi icon={<Wallet size={16} />} label="Spent this month" value={formatPula(stats.spentMonth)} tone={stats.spentMonth > 0 ? 'bad' : 'default'} />
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        {/* Cash chart */}
        <Card className="p-5 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-semibold text-primary dark:text-gray-100">Cash in vs out — last 6 months</h3>
            <div className="flex items-center gap-3 text-xs text-primaryLight dark:text-gray-400">
              <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-brand" /> Received</span>
              <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-sm bg-primaryLight/60" /> Spent</span>
            </div>
          </div>
          <div className="flex h-44 items-end justify-between gap-3">
            {series.rows.map((r) => (
              <div key={r.key} className="flex flex-1 flex-col items-center gap-1">
                <div className="flex h-36 w-full items-end justify-center gap-1">
                  <div className="w-1/2 rounded-t bg-brand transition-all" style={{ height: `${(r.received / series.max) * 100}%` }} title={formatPula(r.received)} />
                  <div className="w-1/2 rounded-t bg-primaryLight/50 transition-all" style={{ height: `${(r.spent / series.max) * 100}%` }} title={formatPula(r.spent)} />
                </div>
                <span className="text-xs text-primaryLight dark:text-gray-400">{r.label}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center justify-between border-t border-black/5 pt-3 text-sm dark:border-white/10">
            <span className="text-primaryLight dark:text-gray-400">Net this month</span>
            <span className={`flex items-center gap-1 font-bold ${stats.net >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {stats.net >= 0 ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
              {formatPula(stats.net)}
            </span>
          </div>
        </Card>

        {/* Recent payments */}
        <Card className="p-5">
          <h3 className="mb-3 font-semibold text-primary dark:text-gray-100">Recent payments</h3>
          {recent.length === 0 ? (
            <p className="py-6 text-center text-sm text-primaryLight dark:text-gray-400">No payments yet.</p>
          ) : (
            <div className="space-y-2">
              {recent.map((p) => (
                <div key={p.id} className="flex items-center justify-between text-sm">
                  <div className="min-w-0">
                    <div className="truncate font-medium text-primary dark:text-gray-100">{p.clientName}</div>
                    <div className="text-xs text-primaryLight dark:text-gray-400">{p.receiptNumber}</div>
                  </div>
                  <span className="font-semibold text-green-600 dark:text-green-400">{formatPula(p.amount)}</span>
                </div>
              ))}
            </div>
          )}
          {stats.pendingQuotes > 0 && (
            <div className="mt-4 flex items-center gap-2 rounded-xl bg-brandSoft px-3 py-2 text-sm text-brand dark:bg-white/5">
              <FileSignature size={15} />
              {stats.pendingQuotes} quote{stats.pendingQuotes === 1 ? '' : 's'} awaiting a response
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
